import { keepPreviousData, useQuery, type QueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { CursorPage, FileRef, IsoDateTime, UserRef } from '@/shared/api/types';
import type { CommentItem } from '@/shared/components/comments';
import type { ReactionSummary, ReactionType } from '@/shared/components/reactions/reactions';

/*
 * Goal maps API (backend module `goals`, route root /api/goals — docs/modules/goals.md).
 * Query keys start with the module id `goals`.
 */

export type GoalPriority = 'important' | 'medium' | 'normal';
export type GoalStatus = 'notStarted' | 'inProgress' | 'achieved' | 'cancelled';
export type ProgressMode = 'manual' | 'children' | 'tasks';
export type MapRole = 'none' | 'viewer' | 'editor' | 'owner';
export type ShareTargetType = 'user' | 'department' | 'group' | 'everyone';
export type ShareRole = 'viewer' | 'editor';
export type ChildrenMode = 'delete' | 'reattach';
export type MapsView = 'all' | 'mine' | 'shared' | 'archived';

export const PRIORITIES: GoalPriority[] = ['important', 'medium', 'normal'];
export const STATUSES: GoalStatus[] = ['notStarted', 'inProgress', 'achieved', 'cancelled'];

export interface MapPermissions {
  canEdit: boolean;
  canManage: boolean;
  canComment: boolean;
}

export interface Share {
  targetType: ShareTargetType;
  targetId: string | null;
  role: ShareRole;
  user: UserRef | null;
  group: { id: string; name: string; avatarUrl: string | null; isProject: boolean } | null;
  /** A secret group the user is not in: neither its id nor its name is shown. */
  hidden?: boolean;
}

export interface GoalRef {
  id: string;
  title: string;
  status: GoalStatus;
  priority: GoalPriority;
  progress: number;
}

export interface MapListItem {
  id: string;
  title: string;
  description: string;
  owner: UserRef;
  myRole: MapRole;
  isArchived: boolean;
  isShared: boolean;
  rootGoal: GoalRef;
  progress: number;
  goalCount: number;
  achievedCount: number;
  overdueCount: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  updatedBy: UserRef | null;
}

/** A goal as the canvas draws it. */
export interface GoalNode {
  id: string;
  parentId: string | null;
  sortOrder: number;
  title: string;
  priority: GoalPriority;
  status: GoalStatus;
  progress: number;
  progressMode: ProgressMode;
  deadline: IsoDateTime | null;
  deadlineHasTime: boolean;
  isOverdue: boolean;
  responsible: UserRef | null;
  x: number;
  y: number;
  isCollapsed: boolean;
  canEdit: boolean;
  /** The title's version: a title edit sends the one it started from (409 when someone changed it meanwhile). */
  titleVersion: number;
  updatedAt: IsoDateTime;
  /** Linked tasks that count (declined ones do not). */
  tasksTotal: number;
  /** Of them completed. */
  tasksDone: number;
}

export interface GoalLink {
  id: string;
  fromId: string;
  toId: string;
  createdAt: IsoDateTime;
}

export interface MapGraph {
  id: string;
  title: string;
  description: string;
  owner: UserRef;
  myRole: MapRole;
  permissions: MapPermissions;
  isArchived: boolean;
  feedOnAchieved: boolean;
  rootGoalId: string;
  templateKey: string | null;
  progress: number;
  revision: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  shares: Share[];
  goals: GoalNode[];
  links: GoalLink[];
}

export interface GoalPermissions {
  edit: boolean;
  updateProgress: boolean;
  editStructure: boolean;
  comment: boolean;
}

export interface LinkedGoal {
  linkId: string;
  goal: GoalRef;
}

export interface GoalDetail {
  id: string;
  map: { id: string; title: string; isArchived: boolean; rootGoalId: string };
  parent: GoalRef | null;
  children: GoalRef[];
  path: GoalRef[];
  title: string;
  descriptionHtml: string;
  deadline: IsoDateTime | null;
  deadlineHasTime: boolean;
  isOverdue: boolean;
  priority: GoalPriority;
  status: GoalStatus;
  statusChangedAt: IsoDateTime | null;
  progressMode: ProgressMode;
  progress: number;
  manualProgress: number;
  childrenProgress: number;
  tasksProgress: number;
  tasksTotal: number;
  tasksDone: number;
  responsible: UserRef | null;
  dependsOn: LinkedGoal[];
  dependents: LinkedGoal[];
  commentCount: number;
  createdBy: UserRef | null;
  createdAt: IsoDateTime;
  updatedBy: UserRef | null;
  updatedAt: IsoDateTime;
  /** The title's version (a title edit sends the one it started from). */
  titleVersion: number;
  /** The description's version (a description edit sends the one it started from). */
  descriptionVersion: number;
  permissions: GoalPermissions;
  /** How each child weighs in this goal's progress: the weight set on it and the share it has now, in percent. */
  childWeights: ChildWeight[];
}

export interface ChildWeight {
  goalId: string;
  /** The weight set on the child (percent), null — none: it weighs the mean of its siblings' weights. */
  weight: number | null;
  /** What the child weighs now, in whole percents that add up to 100 (null for a cancelled child, which is left out). */
  share: number | null;
}

export interface SetWeightsInput {
  /** Every child that counts with a non-negative number; the server turns them into percents that add up to 100. */
  weights?: { goalId: string; weight: number }[];
  /** Every child weighs the same again. */
  reset?: boolean;
}

export type ActivityKind =
  | 'created'
  | 'title'
  | 'description'
  | 'deadline'
  | 'priority'
  | 'status'
  | 'progress'
  | 'progressMode'
  | 'responsible'
  | 'parent'
  | 'linkAdded'
  | 'linkRemoved'
  | 'deleted'
  | 'restored'
  | 'taskLinked'
  | 'taskUnlinked'
  | 'weights';

export interface ActivityEntry {
  id: string;
  kind: ActivityKind;
  actor: UserRef | null;
  oldValue: string | null;
  newValue: string | null;
  oldUser: UserRef | null;
  newUser: UserRef | null;
  goal: GoalRef | null;
  createdAt: IsoDateTime;
  /** A linked / unlinked task the reader may see (null: hidden from them; the log keeps no titles). */
  task?: { id: string; number: number; title: string; url: string } | null;
}

export interface MapTemplate {
  key: string;
  title: string;
  description: string;
  goalCount: number;
  preview: string[];
}

export interface Deletion {
  deletionId: string;
  deletedIds: string[];
  reattachedIds: string[];
  removedLinkIds: string[];
  revision: number;
}

export interface Restored {
  restoredIds: string[];
  restoredLinkIds: string[];
  revision: number;
}

export interface GoalPlace {
  id: string;
  parentId: string | null;
  sortOrder: number;
  x: number;
  y: number;
  isCollapsed: boolean;
}

export interface Moves {
  goals: GoalPlace[];
  revision: number;
  /** "Depends on" links removed because a goal moved under the goal it was linked with. */
  removedLinks?: GoalLink[];
}

export interface GoalCounters {
  overdue: number;
  dueSoon: number;
}

/** Task statuses of the tasks module (its wire values). */
export type TaskStatus = 'pending' | 'inProgress' | 'awaitingReview' | 'completed' | 'deferred' | 'declined';

/** A task linked to a goal (only tasks the user may see are listed). */
export interface LinkedTask {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  priority: 'normal' | 'high';
  deadline: IsoDateTime | null;
  isOverdue: boolean;
  responsible: UserRef | null;
  /** The task card's deep link. */
  url: string;
}

export interface GoalTasks {
  items: LinkedTask[];
  /** Linked tasks that count for the progress, seen or not. */
  total: number;
  done: number;
  /** Linked tasks the user may not see. */
  hidden: number;
  /** May create, link and unlink tasks. */
  canLink: boolean;
}

export interface CreateGoalTaskInput {
  title: string;
  descriptionHtml?: string | null;
  responsibleId?: string | null;
  deadline?: IsoDateTime | null;
  groupId?: string | null;
  priority?: 'normal' | 'high';
}

/** A tab on a map: who, the goal it focuses (card open or title edited) and whether it is typing. */
export interface MapViewer {
  user: UserRef;
  clientId: string;
  goalId: string | null;
  editing: boolean;
  expiresAt: IsoDateTime;
}

export interface DragGoal {
  goalId: string;
  x: number;
  y: number;
}

export interface PersonGoal {
  id: string;
  title: string;
  status: GoalStatus;
  priority: GoalPriority;
  progress: number;
  deadline: IsoDateTime | null;
  deadlineHasTime: boolean;
  isOverdue: boolean;
  map: { id: string; title: string; isArchived: boolean; rootGoalId: string };
  isRoot: boolean;
}

export interface PersonGoals {
  items: PersonGoal[];
  open: number;
  overdue: number;
  achieved: number;
}

export interface GoalComment extends CommentItem {
  attachments: FileRef[];
}

export interface CreateMapInput {
  title: string;
  description?: string | null;
  rootTitle?: string | null;
  templateKey?: string | null;
}

export interface UpdateMapInput {
  title?: string;
  description?: string | null;
  feedOnAchieved?: boolean;
}

export interface CreateGoalInput {
  parentId: string;
  title: string;
  afterId?: string | null;
  x?: number;
  y?: number;
  priority?: GoalPriority;
}

/** PATCH: absent = unchanged, null = clear. Text edits send the version of the text they started from. */
export interface UpdateGoalInput {
  titleVersion?: number;
  descriptionVersion?: number;
  title?: string;
  descriptionHtml?: string | null;
  deadline?: IsoDateTime | null;
  deadlineHasTime?: boolean;
  priority?: GoalPriority;
  status?: GoalStatus;
  progressMode?: ProgressMode;
  progress?: number;
  responsibleId?: string | null;
  isCollapsed?: boolean;
}

export interface GoalMove {
  goalId: string;
  x?: number;
  y?: number;
  parentId?: string;
  index?: number;
}

export interface ShareInput {
  targetType: ShareTargetType;
  targetId?: string | null;
  role: ShareRole;
}

export const goalKeys = {
  all: ['goals'] as const,
  lists: () => ['goals', 'maps'] as const,
  list: (view: MapsView, search: string | undefined, groupId?: string) =>
    ['goals', 'maps', view, search ?? '', groupId ?? ''] as const,
  templates: (language: string) => ['goals', 'templates', language] as const,
  counters: ['goals', 'counters'] as const,
  map: (id: string) => ['goals', 'map', id] as const,
  goals: () => ['goals', 'goal'] as const,
  goal: (id: string) => ['goals', 'goal', id] as const,
  activity: (goalId: string) => ['goals', 'activity', goalId] as const,
  mapActivity: (mapId: string) => ['goals', 'map-activity', mapId] as const,
  comments: (goalId: string) => ['goals', 'comments', goalId] as const,
  tasks: (goalId: string) => ['goals', 'tasks', goalId] as const,
  taskCandidates: (goalId: string, search: string) => ['goals', 'task-candidates', goalId, search] as const,
  person: (userId: string, view: 'open' | 'all') => ['goals', 'person', userId, view] as const,
  shares: (mapId: string) => ['goals', 'shares', mapId] as const,
};

const enc = encodeURIComponent;

export const goalsApi = {
  maps: (view: MapsView, search: string | undefined, groupId: string | undefined, signal?: AbortSignal) =>
    api.get<MapListItem[]>('/goals/maps', {
      query: { view: view === 'all' ? undefined : view, search, groupId },
      signal,
    }),
  templates: (signal?: AbortSignal) => api.get<MapTemplate[]>('/goals/templates', { signal }),
  counters: (signal?: AbortSignal) => api.get<GoalCounters>('/goals/counters', { signal }),
  createMap: (input: CreateMapInput) => api.post<MapGraph>('/goals/maps', input),
  map: (id: string, signal?: AbortSignal) => api.get<MapGraph>(`/goals/maps/${enc(id)}`, { signal }),
  updateMap: (id: string, input: UpdateMapInput) => api.patch<MapGraph>(`/goals/maps/${enc(id)}`, input),
  deleteMap: (id: string) => api.delete(`/goals/maps/${enc(id)}`),
  archive: (id: string, archived: boolean) =>
    api.post(`/goals/maps/${enc(id)}/${archived ? 'archive' : 'unarchive'}`),
  duplicate: (id: string, title?: string) =>
    api.post<MapGraph>(`/goals/maps/${enc(id)}/duplicate`, { title }),
  shares: (id: string, signal?: AbortSignal) => api.get<Share[]>(`/goals/maps/${enc(id)}/shares`, { signal }),
  replaceShares: (id: string, shares: ShareInput[]) =>
    api.put<Share[]>(`/goals/maps/${enc(id)}/shares`, { shares }),
  mapActivity: (id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<ActivityEntry>>(`/goals/maps/${enc(id)}/activity`, {
      query: { cursor: cursor ?? undefined },
      signal,
    }),

  createGoal: (mapId: string, input: CreateGoalInput) =>
    api.post<GoalNode>(`/goals/maps/${enc(mapId)}/goals`, input),
  goal: (id: string, signal?: AbortSignal) => api.get<GoalDetail>(`/goals/${enc(id)}`, { signal }),
  updateGoal: (id: string, input: UpdateGoalInput) => api.patch<GoalDetail>(`/goals/${enc(id)}`, input),
  setWeights: (id: string, input: SetWeightsInput) => api.put<GoalDetail>(`/goals/${enc(id)}/weights`, input),
  declineResponsibility: (id: string) => api.post(`/goals/${enc(id)}/decline`, {}),
  deleteGoal: (id: string, children?: ChildrenMode) =>
    api.delete<Deletion>(`/goals/${enc(id)}`, { query: { children } }),
  restore: (mapId: string, deletionId: string) =>
    api.post<Restored>(`/goals/maps/${enc(mapId)}/deletions/${enc(deletionId)}/restore`, {}),
  move: (mapId: string, moves: GoalMove[]) => api.post<Moves>(`/goals/maps/${enc(mapId)}/moves`, { moves }),
  arrange: (mapId: string) => api.post<Moves>(`/goals/maps/${enc(mapId)}/arrange`, {}),
  link: (mapId: string, fromId: string, toId: string) =>
    api.post<GoalLink>(`/goals/maps/${enc(mapId)}/links`, { fromId, toId }),
  unlink: (mapId: string, linkId: string) => api.delete(`/goals/maps/${enc(mapId)}/links/${enc(linkId)}`),
  activity: (goalId: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<ActivityEntry>>(`/goals/${enc(goalId)}/activity`, {
      query: { cursor: cursor ?? undefined },
      signal,
    }),

  comments: (goalId: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<GoalComment>>(`/goals/${enc(goalId)}/comments`, {
      query: { cursor: cursor ?? undefined },
      signal,
    }),
  createComment: (goalId: string, bodyHtml: string, attachmentIds: string[]) =>
    api.post<GoalComment>(`/goals/${enc(goalId)}/comments`, { bodyHtml, attachmentIds }),
  updateComment: (goalId: string, commentId: string, bodyHtml: string, attachmentIds: string[]) =>
    api.put<GoalComment>(`/goals/${enc(goalId)}/comments/${enc(commentId)}`, { bodyHtml, attachmentIds }),
  deleteComment: (goalId: string, commentId: string) =>
    api.delete(`/goals/${enc(goalId)}/comments/${enc(commentId)}`),
  react: (goalId: string, commentId: string, type: ReactionType) =>
    api.post<ReactionSummary[]>(`/goals/${enc(goalId)}/comments/${enc(commentId)}/reactions`, { type }),

  tasks: (goalId: string, signal?: AbortSignal) =>
    api.get<GoalTasks>(`/goals/${enc(goalId)}/tasks`, { signal }),
  createTask: (goalId: string, input: CreateGoalTaskInput) =>
    api.post<LinkedTask>(`/goals/${enc(goalId)}/tasks`, input),
  linkTask: (goalId: string, taskId: string) =>
    api.post<LinkedTask>(`/goals/${enc(goalId)}/tasks/link`, { taskId }),
  unlinkTask: (goalId: string, taskId: string) => api.delete(`/goals/${enc(goalId)}/tasks/${enc(taskId)}`),
  taskCandidates: (goalId: string, search: string, signal?: AbortSignal) =>
    api.get<LinkedTask[]>(`/goals/${enc(goalId)}/task-candidates`, { query: { search }, signal }),

  presence: (mapId: string, signal?: AbortSignal) =>
    api.get<MapViewer[]>(`/goals/maps/${enc(mapId)}/presence`, { signal }),
  heartbeat: (mapId: string, input: { clientId: string; goalId?: string | null; editing?: boolean }) =>
    api.post<MapViewer[]>(`/goals/maps/${enc(mapId)}/presence`, input),
  leave: (mapId: string, clientId: string) =>
    api.delete(`/goals/maps/${enc(mapId)}/presence/${enc(clientId)}`),
  drag: (mapId: string, input: { clientId: string; goals: DragGoal[]; done?: boolean }) =>
    api.post(`/goals/maps/${enc(mapId)}/drag`, input),

  personGoals: (userId: string, view: 'open' | 'all', signal?: AbortSignal) =>
    api.get<PersonGoals>(`/goals/people/${enc(userId)}/goals`, {
      query: { view: view === 'open' ? undefined : view },
      signal,
    }),
};

export function useMaps(view: MapsView, search: string | undefined, groupId?: string) {
  return useQuery({
    queryKey: goalKeys.list(view, search, groupId),
    queryFn: ({ signal }) => goalsApi.maps(view, search, groupId, signal),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useMapGraph(id: string | undefined) {
  return useQuery({
    queryKey: goalKeys.map(id ?? ''),
    queryFn: ({ signal }) => goalsApi.map(id ?? '', signal),
    enabled: !!id,
  });
}

export function useGoal(id: string) {
  return useQuery({ queryKey: goalKeys.goal(id), queryFn: ({ signal }) => goalsApi.goal(id, signal) });
}

export function useGoalTasks(goalId: string) {
  return useQuery({
    queryKey: goalKeys.tasks(goalId),
    queryFn: ({ signal }) => goalsApi.tasks(goalId, signal),
  });
}

export function useMapShares(mapId: string, enabled = true) {
  return useQuery({
    queryKey: goalKeys.shares(mapId),
    queryFn: ({ signal }) => goalsApi.shares(mapId, signal),
    enabled,
  });
}

export function usePersonGoals(userId: string, view: 'open' | 'all') {
  return useQuery({
    queryKey: goalKeys.person(userId, view),
    queryFn: ({ signal }) => goalsApi.personGoals(userId, view, signal),
    placeholderData: keepPreviousData,
  });
}

export function useGoalCounters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: goalKeys.counters,
    queryFn: ({ signal }) => goalsApi.counters(signal),
    enabled: options.enabled ?? true,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

/** Writes changed goal nodes into a cached graph (fields given replace the node's). */
export function patchGraphGoals(
  queryClient: QueryClient,
  mapId: string,
  patch: (goal: GoalNode) => GoalNode | null,
): void {
  queryClient.setQueryData<MapGraph>(goalKeys.map(mapId), (graph) => {
    if (!graph) return graph;
    const goals = graph.goals.flatMap((goal) => {
      const next = patch(goal);
      return next ? [next] : [];
    });
    const changed = goals.length !== graph.goals.length || goals.some((goal, i) => goal !== graph.goals[i]);
    return changed ? { ...graph, goals } : graph;
  });
}

/** After a change of a goal: the card and the canvas refresh, and the counters and lists that show it. */
export function invalidateGoal(queryClient: QueryClient, mapId: string, goalId?: string): void {
  if (goalId) {
    void queryClient.invalidateQueries({ queryKey: goalKeys.goal(goalId) });
    void queryClient.invalidateQueries({ queryKey: goalKeys.activity(goalId) });
  }
  void queryClient.invalidateQueries({ queryKey: goalKeys.map(mapId) });
  void queryClient.invalidateQueries({ queryKey: goalKeys.mapActivity(mapId) });
  void queryClient.invalidateQueries({ queryKey: goalKeys.lists() });
  void queryClient.invalidateQueries({ queryKey: goalKeys.counters });
}

/**
 * The goals tab of the Tasks page: the maps list. A map is a page of its own below it (`<GOALS_PATH>/maps/:mapId`). The old
 * addresses `/goals/...` redirect here.
 */
export const GOALS_PATH = '/tasks/goals';

/** SPA path of a map (with a goal's card open). */
export const mapPath = (mapId: string, goalId?: string) =>
  goalId ? `${GOALS_PATH}/maps/${mapId}?panel=goal:${goalId}` : `${GOALS_PATH}/maps/${mapId}`;
